import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';
import { REGULATION_META, REGULATION_ORDER } from '@/pages/regulations/data';
import type { RegulationItem } from '@/pages/regulations/data';

interface CategoryText {
  id: string;
  label: string;
  title: string;
  badge: string;
  items: RegulationItem[];
}

export default function RegulationsSection() {
  const { t } = useTranslation();
  const rawCategories = t('regulations.categories', { returnObjects: true }) as CategoryText[];

  const categories = rawCategories.map((cat) => ({
    ...cat,
    icon: REGULATION_META[cat.id].icon,
    image: REGULATION_META[cat.id].image,
  }));

  const [active, setActive] = useState<string>(REGULATION_ORDER[0]);
  const headerRef = useScrollReveal<HTMLDivElement>();
  const contentRef = useScrollReveal<HTMLDivElement>();
  const current = categories.find((c) => c.id === active) ?? categories[0];

  return (
    <section id="regulations" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-12 reveal-on-scroll animate-fade-in-up">
          <span className="inline-block bg-teal-50 text-teal-600 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
            {t('regulations.tag')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            {t('regulations.title1')}<br />
            <span className="text-teal-500">{t('regulations.title2')}</span>
          </h2>
          <p className="mt-5 text-gray-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t('regulations.subtitle')}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActive(cat.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                active === cat.id
                  ? 'bg-teal-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={cat.icon} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div ref={contentRef} className="reveal-on-scroll animate-fade-in-up grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Image Card */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden h-[280px] lg:h-full min-h-[360px]">
            <img
              src={current.image}
              alt={current.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="inline-block bg-teal-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
                {current.badge}
              </span>
              <h3 className="text-white font-bold text-xl leading-snug">{current.title}</h3>
            </div>
          </div>

          {/* Items */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            {current.items.map((item) => (
              <div key={item.name} className="bg-gray-50 border border-gray-100 rounded-2xl p-5 hover:border-teal-200 transition-colors duration-200">
                <div className="flex flex-wrap items-start gap-3 mb-3">
                  <h4 className="font-semibold text-gray-900 text-base">{item.name}</h4>
                </div>
                <div className="flex flex-wrap gap-4 mb-3">
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <i className="ri-government-line text-gray-400" />
                    {item.authority}
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <i className="ri-refresh-line text-gray-400" />
                    {item.renewal}
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed border-t border-gray-100 pt-3">
                  {item.note}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* View Full Guide CTA */}
        <div className="mt-10 flex justify-center">
          <Link
            to="/regulations"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200 whitespace-nowrap"
          >
            {t('regulations.viewFullGuide')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-teal-500">
            <i className="ri-information-line text-xl" />
          </div>
          <p className="text-gray-900 text-sm leading-relaxed">
            <strong>{t('regulations.disclaimerTitle')}</strong> {t('regulations.disclaimerText')}
          </p>
        </div>
      </div>
    </section>
  );
}